import { useEffect, useState } from "react";
import { auth, db } from "../firebaseConfig";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

export default function MemberLoginOTP() {
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ IMPORTANT: use a dedicated global key for this page to avoid conflicts with other pages
  const RECAPTCHA_KEY = "__member_login_otp_recaptcha__";

  // ✅ UPDATED: robust recaptcha (render + stale cleanup)
  const ensureRecaptcha = async () => {
    const id = "recaptcha-container";
    const el = document.getElementById(id);
    if (!el) throw new Error("reCAPTCHA container missing");

    // ✅ reuse existing instance (page-specific)
    if (window[RECAPTCHA_KEY]) return window[RECAPTCHA_KEY];

    // ✅ clear stale DOM (old iframe/widgets)
    if (el.childNodes && el.childNodes.length > 0) {
      try {
        el.innerHTML = "";
      } catch {}
    }

    window[RECAPTCHA_KEY] = new RecaptchaVerifier(auth, id, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        try {
          window[RECAPTCHA_KEY]?.clear?.();
        } catch {}
        window[RECAPTCHA_KEY] = null;
        const node = document.getElementById(id);
        if (node) node.innerHTML = "";
      },
    });

    await window[RECAPTCHA_KEY].render();
    return window[RECAPTCHA_KEY];
  };

  const sendOtp = async () => {
    const m = String(mobile || "").trim();
    if (!/^\d{10}$/.test(m)) return alert("Enter valid 10-digit mobile number.");

    setLoading(true);
    try {
      const appVerifier = await ensureRecaptcha(); // ✅ FIX: await + render
      if (!appVerifier) throw new Error("reCAPTCHA not ready");

      const phone = `+91${m}`;
      const result = await signInWithPhoneNumber(auth, phone, appVerifier);
      setConfirmResult(result);
      setOtpSent(true);
      alert("✅ OTP sent");
    } catch (e) {
      console.error(e);
      alert("❌ OTP send failed: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmResult) return alert("Send OTP first");
    if (!/^\d{6}$/.test(String(otp || ""))) return alert("Enter valid 6-digit OTP");

    setLoading(true);
    try {
      await confirmResult.confirm(otp);

      // ✅ Allow login only if member exists + approved
      const m = String(mobile || "").trim();
      const qRef = query(collection(db, "members"), where("mobile", "==", m));
      const snap = await getDocs(qRef);

      if (snap.empty) {
        alert("❌ No membership found for this mobile number.");
        return;
      }

      // pick latest
      const docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      docs.sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));
      const mem = docs[0];

      const approved = String(mem.status || "").toLowerCase() === "approved";
      const paid = String(mem.payment_status || "").toUpperCase() === "PAID";

      if (!approved || !paid) {
        alert("⚠️ Your membership is not approved/paid yet. Please complete payment or contact office.");
        return;
      }

      // ✅ Save session hints (optional)
      localStorage.setItem("member_mobile", m);
      localStorage.setItem("member_doc_id", mem.id);
      localStorage.setItem("member_id", mem.member_id || "");

      alert("✅ Login successful");
      window.location.href = "/member/dashboard"; // <-- apna route yaha set karo
    } catch (e) {
      console.error(e);
      alert("❌ OTP verify failed: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // reset otp if mobile changes
    setOtpSent(false);
    setOtp("");
    setConfirmResult(null);
  }, [mobile]);

  // ✅ ADD: cleanup on unmount (prevents stale recaptcha errors)
  useEffect(() => {
    return () => {
      try {
        window[RECAPTCHA_KEY]?.clear?.();
      } catch {}
      window[RECAPTCHA_KEY] = null;
      const el = document.getElementById("recaptcha-container");
      if (el) el.innerHTML = "";
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow p-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">
          Member Login
        </div>
        <h1 className="text-xl font-extrabold text-slate-900">Login with Mobile OTP</h1>
        <p className="text-xs text-slate-600 mt-1">
          Approved members only. OTP will be sent to your registered mobile number.
        </p>

        <div className="mt-5">
          <label className="block text-sm font-bold text-slate-800 mb-1">Mobile Number</label>
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="10-digit mobile"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
          />
        </div>

        <div id="recaptcha-container"></div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={sendOtp}
            disabled={loading}
            className={cx(
              "flex-1 rounded-lg px-4 py-2 text-sm font-bold text-white",
              "bg-slate-900 hover:bg-slate-800 disabled:opacity-60"
            )}
          >
            {otpSent ? "Resend OTP" : "Send OTP"}
          </button>
        </div>

        {otpSent && (
          <div className="mt-3">
            <label className="block text-sm font-bold text-slate-800 mb-1">OTP</label>
            <div className="flex gap-2">
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit OTP"
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
              <button
                type="button"
                onClick={verifyOtp}
                disabled={loading}
                className="rounded-lg px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              >
                Verify
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
